/**
 * Sticky add to cart bar.
 *
 * Shows once the main add to cart button has scrolled above the viewport. The
 * button submits the main product form, so the bar uses the same variant,
 * quantity and cart popup as the main button. It mirrors the main button's
 * disabled/sold out state and the main price. Without the main form (or
 * without requestSubmit) it falls back to its own form with the selected variant.
 */
class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.onSubmit = this.onSubmit.bind(this);
    this.observers = [];
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('.sticky-atc__button');
    this.label = this.button ? this.button.querySelector('span') : null;
    this.defaultLabel = this.label ? this.label.textContent.trim() : '';
    this.variantInput = this.querySelector('input[name="id"]');
    this.priceContainer = this.querySelector('.sticky-atc__price');

    if (this.form) this.form.addEventListener('submit', this.onSubmit);

    this.watchTarget();
    this.mirrorMainButton();
    this.mirrorPrice();
  }

  disconnectedCallback() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    document.documentElement.style.removeProperty('--sticky-atc-height');
  }

  get mainForm() {
    return document.querySelector('product-form form[action*="/cart/add"]');
  }

  get mainButton() {
    const form = this.mainForm;
    return form ? form.querySelector('[type="submit"][name="add"]') : null;
  }

  get mainPrice() {
    return document.querySelector('product-info [id^="price-"]');
  }

  /**
   * The bar shows once the main buy-button area is entirely above the viewport.
   * Checked on scroll and resize rather than with an IntersectionObserver: an
   * observer only reports crossings, so a jump from below the fold straight past
   * the button (restored scroll position, back navigation, fast flick) never fires.
   */
  watchTarget() {
    this.target = document.querySelector('.product-form__buttons') || document.querySelector('product-form');
    if (!this.target) {
      this.toggle(false);
      return;
    }

    this.scheduled = false;
    this.onScroll = () => {
      if (this.scheduled) return;
      this.scheduled = true;
      requestAnimationFrame(() => {
        this.scheduled = false;
        this.update();
      });
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  update() {
    if (!this.target || !this.target.isConnected) {
      this.target = document.querySelector('.product-form__buttons') || document.querySelector('product-form');
      if (!this.target) return;
    }
    this.toggle(this.target.getBoundingClientRect().bottom < 0);
  }

  toggle(visible) {
    const active = visible === true;
    if (this.active === active) return;
    this.active = active;
    this.classList.toggle('sticky-atc--active', active);
    this.setAttribute('aria-hidden', active ? 'false' : 'true');
    // Reserve room at the bottom of the page so the bar never covers the footer.
    if (active) {
      document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
    } else {
      document.documentElement.style.removeProperty('--sticky-atc-height');
    }
  }

  /** Follow the main button: disabled and "Sold out" when it is. */
  mirrorMainButton() {
    const mainButton = this.mainButton;
    if (!mainButton || !this.button) return;

    const sync = () => {
      const disabled = mainButton.hasAttribute('disabled');
      this.button.toggleAttribute('disabled', disabled);
      if (this.label) {
        const mainLabel = mainButton.querySelector('span');
        this.label.textContent = disabled && mainLabel ? mainLabel.textContent.trim() : this.defaultLabel;
      }
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(mainButton, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
    this.observers.push(observer);
  }

  /** Copy the main price whenever the selected variant changes it. */
  mirrorPrice() {
    const mainPrice = this.mainPrice;
    if (!mainPrice || !this.priceContainer) return;

    const sync = () => {
      this.priceContainer.innerHTML = mainPrice.innerHTML;
    };

    const observer = new MutationObserver(sync);
    observer.observe(mainPrice, { childList: true, subtree: true, characterData: true });
    this.observers.push(observer);
  }

  onSubmit(event) {
    const mainForm = this.mainForm;
    const mainButton = this.mainButton;

    if (mainForm && typeof mainForm.requestSubmit === 'function') {
      event.preventDefault();
      if (mainButton && (mainButton.hasAttribute('disabled') || mainButton.getAttribute('aria-disabled') === 'true')) return;
      mainForm.requestSubmit();
      return;
    }

    // Fallback: submit this form with the variant selected in the main form.
    const mainInput = mainForm ? mainForm.querySelector('input[name="id"]') : null;
    if (mainInput && mainInput.value && this.variantInput) {
      this.variantInput.value = mainInput.value;
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
